import styles from './styles.module.scss';
import WithdrawResponseContent from './Content';
import { usePathname, useSearchParams } from '@starsoft/common/hooks';
import { Nullable } from '@starsoft/common/interfaces';
import { Navigate } from '@starsoft/common/components';

/**
 * WithdrawResponse Component
 * This component handles the response view for the withdrawal process.
 *
 * Constants & Hooks:
 * - pathname: Retrieved from usePathname hook, represents the current path.
 * - search: Retrieved from useSearchParams hook, represents the URL search parameters.
 * - id: Extracted from search parameters, represents the withdrawal ID.
 *
 * Logic scope:
 * - Checks if the withdrawal ID is present in the search parameters.
 * - If the ID is not present, navigates back to the current pathname.
 * - If the ID is present, renders the WithdrawResponseContent component.
 *
 * @returns {JSX.Element} The response content for the withdrawal process or a navigation component.
 *
 * @example
 * // If the URL contains the search parameter 'withdraw-id', it returns:
 * <div className={styles.container}>
 *   <WithdrawResponseContent />
 * </div>
 * // If the URL does not contain the search parameter 'withdraw-id', it returns:
 * <Navigate to={pathname} />
 */
export default function WithdrawResponse() {
  // Retrieve the current pathname using the usePathname hook
  const pathname: string = usePathname();
  // Retrieve the URL search parameters using the useSearchParams hook
  const search: URLSearchParams = useSearchParams();
  // Extract the 'withdraw-id' parameter from the search parameters
  const id: Nullable<string> = search.get('withdraw-id');

  // If the 'withdraw-id' parameter is not present, navigate back to the current pathname
  if (!id) {
    return <Navigate to={pathname} />;
  }

  // If the 'withdraw-id' parameter is present, render the WithdrawResponseContent component
  return (
    <div className={styles.container}>
      <WithdrawResponseContent />
    </div>
  );
}
